import React, { useEffect, useState } from "react";
import Link from "next/link";
import { useInView } from "react-intersection-observer";
import LazyImage from "../LazyImage";
import FocusTrap from "focus-trap-react";

const VignetteItem = (props) => {
  const { image, video, heading, playVideo } = props;
  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <button
        onClick={() => playVideo(video)}
        className="sm:tw-grayscale tw-cursor-pointer tw-group/vignette hover:tw-grayscale-0 tw-transition-all tw-duration-500 tw-overflow-clip tw-grid tw-grid-cols-1 tw-grid-rows-1 tw-items-end tw-justify-items-start tw-p-0 tw-border-none tw-bg-transparent tw-rounded-xl"
      >
        {/* Play Icon */}
        <div className="tw-row-start-1 tw-col-start-1 tw-z-10 tw-opacity-50 tw-scale-75 group-hover/vignette:tw-opacity-90 group-hover/vignette:tw-scale-110 tw-transition-all tw-duration-300 tw-rounded-full tw-pl-6 tw-pb-6">
          <svg
            width="34"
            height="34"
            viewBox="0 0 30 34"
            xmlns="http://www.w3.org/2000/svg"
            className="tw-drop-shadow-md"
          >
            <path
              d="M0 0L30 17L0 34V0Z"
              fill="#ffffff"
              className="tw-shadow-xl"
            />
          </svg>
        </div>
        {/* Image */}
        <div className="tw-row-start-1 tw-col-start-1 tw-w-full tw-h-full">
          <div className="group-hover/vignette:tw-scale-110 tw-transition-all tw-duration-500">
            <LazyImage src={image} />
          </div>
        </div>
      </button>
      {heading && (
        <p className="tw-m-0tw-transition-all tw-duration-300 tw-leading-tight tw-text-left">
          {heading}
        </p>
      )}
    </div>
  );
};

const Vignette = (props) => {
  const [slideUpRef, isInView] = useInView({ triggerOnce: true });

  const {
    heading,
    copy,
    videos,
    linkText,
    linkUrl,
    target,
    className = "",
  } = props;

  const [activeVideo, setActiveVideo] = useState(null);
  const playVideo = (video) => {
    setActiveVideo(video);
  };

  useEffect(() => {
    if (!activeVideo) return;

    const originalOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [activeVideo]);

  return (
    <div className={`container-fluid ${className}`}>
      {/*uses .animate to start animation*/}

      {/* Copy, Grid, and CTA */}
      <div className="row">
        <div
          ref={slideUpRef}
          className={`col-12  animation ${
            isInView ? "slide-up" : ""
          } tw-flex tw-flex-col tw-items-center tw-gap-[40px] tw-max-w-[1100px] mx-auto`}
        >
          {/* Title and copy */}
          <div className="tw-flex tw-flex-col tw-items-center tw-gap-4 tw-text-center">
            <h2 className="heading2">{heading}</h2>
            <div
              className="tw-text-center"
              dangerouslySetInnerHTML={{ __html: copy }}
            />
          </div>

          {/* Vignette Image Link Grid */}
          <div className="tw-grid sm:tw-grid-cols-3 tw-auto-rows-auto tw-gap-10 tw-items-start">
            {videos.map((item) => (
              <VignetteItem
                image={item.vignette_image.url}
                video={item.vignette_video_source_url}
                heading={item.vignette_heading}
                playVideo={playVideo}
              />
            ))}
          </div>

          {/* If there is a linkUrl, then it will show the button, otherwise a button won't display*/}
          {linkUrl && (
            <p
              className={`vignette__link ${isInView ? "slide-up" : ""} tw-mt-2`}
            >
              {target == "_blank" ? (
                <a className="link" href={linkUrl} target="_blank">
                  {linkText}
                </a>
              ) : (
                <Link href={linkUrl} passHref>
                  <a className="link" target="_self">
                    {linkText}
                  </a>
                </Link>
              )}
            </p>
          )}
        </div>
      </div>

      {/* Vignette Modal */}
      {activeVideo && (
        <FocusTrap>
          <div className="tw-bg-black/65 tw-w-full tw-h-full tw-fixed tw-top-0 tw-left-0 tw-z-10 tw-overflow-hidden p-5">
            {/* X Icon */}
            <button
              className="tw-w-10 tw-h-10 tw-drop-shadow-xl tw-absolute tw-top-10 tw-left-10 tw-bg-transparent tw-border-none tw-p-0 hover:tw-scale-110 tw-cursor-pointer"
              onClick={() => setActiveVideo(null)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="none"
                stroke="white"
                strokeWidth="2"
              >
                <line x1="4" y1="4" x2="16" y2="16" />
                <line x1="16" y1="4" x2="4" y2="16" />
              </svg>
            </button>
            {/* Video */}
            <iframe
              src={activeVideo}
              tabIndex={0}
              className="tw-w-full tw-h-full tw-border-none"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              title="Vimeo video"
            />
          </div>
        </FocusTrap>
      )}

      <style jsx>{`
        .slide-up {
          animation-name: slide-up;
          animation-duration: 1s;
          animation-iteration-count: 1;
        }

        .vignette__link {
          animation-delay: 100ms;
          padding: 0;
        }

        @keyframes slide-up {
          from {
            opacity: 0;
            visibility: hidden;
            transform: translateY(50px);
          }

          to {
            opacity: 1;
            visibility: visible;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default Vignette;
